import { ref, type Ref } from 'vue';
import { ElMessage } from 'element-plus';
import { maintainExpandMode } from './expandMode';
import { documentDownloadUrl, downloadPolicyBundle, downloadQuotePdf, type QuoteSelection } from './downloads';
import type { EmissionSuccessProps } from './types';

interface DownloadJob {
  run: () => Promise<void>;
  successMessage: string;
  logLabel: string;
  fallbackError: string;
}

const runDownload = async (isDownloading: Ref<boolean>, job: DownloadJob): Promise<void> => {
  isDownloading.value = true;
  try {
    await job.run();
    ElMessage.success(job.successMessage);
  } catch (error) {
    console.error(job.logLabel, error);
    ElMessage.error(error instanceof Error ? error.message : job.fallbackError);
  } finally {
    isDownloading.value = false;
    maintainExpandMode();
  }
};

const handleDownloadDocument = (guidDocumento: string, _nombre?: string): void => {
  if (!guidDocumento) {
    ElMessage.warning('No se puede descargar el documento: GUID no disponible');
    maintainExpandMode();
    return;
  }

  try {
    const url = documentDownloadUrl(guidDocumento);
    console.log('📥 Descargando documento:', url);
    window.open(url, '_blank');
    ElMessage.success('Descarga iniciada');
  } catch (error) {
    console.error('❌ Error al descargar documento:', error);
    ElMessage.error('Error al descargar el documento');
  }
  maintainExpandMode();
};

/** Policy bundle download when a policy was emitted, quote PDF otherwise. */
export const useEmissionDownloads = (props: EmissionSuccessProps & QuoteSelection) => {
  const isDownloadingAll = ref(false);

  const handleDownloadAll = async (): Promise<void> => {
    const idCotizacion = props.quotesResponse?.Cobertura?.Id_Cotizacion;
    const idPoliza = props.policyData?.idPoliza;

    if (props.allowEmission && idPoliza) {
      await runDownload(isDownloadingAll, {
        run: () => downloadPolicyBundle(idPoliza, props.documentos),
        successMessage: 'Todos los archivos se están descargando',
        logLabel: '❌ Error al descargar archivos:',
        fallbackError: 'Error al descargar los archivos',
      });
    } else if (idCotizacion) {
      await runDownload(isDownloadingAll, {
        run: () => downloadQuotePdf(idCotizacion, props),
        successMessage: 'Cotización descargada correctamente',
        logLabel: '❌ Error al descargar cotización:',
        fallbackError: 'Error al descargar la cotización',
      });
    } else {
      ElMessage.warning('No se puede descargar: información no disponible');
      maintainExpandMode();
    }
  };

  return { isDownloadingAll, handleDownloadDocument, handleDownloadAll };
};
